import { Box, Container, Skeleton, Stack } from '@mui/material';
import type { ApprovalFlowInvoiceTypes, GetApprovalFlowInvoicesListResponse } from '@vy/api-definition';
import type { ItemOf } from '@vy/utils';
import { isNotEmptyArray, isNotNullOrUndefined, isNullOrUndefined } from '@vy/utils';
import { useEffect, useState } from 'react';
import { VyLoader } from '../../../components/new-design/feedback/Loader/VyLoader.tsx';
import { VyPageLayout } from '../../../components/new-design/layout/VyPageLayout';
import { withGlobalSearchParams } from '../../../components/utility/GlobalSearchParamsBoundary';
import { useDynamicInvoice } from '../../../hooks/requests/use-dynamic-invoice';
import { useGetApprovalFlowInvoicesList } from '../../../hooks/requests/use-get-approval-flow-invoices-list';
import { useToggle } from '../../../hooks/use-toggle';
import { DynamicInvoiceViewPage } from '../DynamicInvoiceViewPage';
import { ApprovalFlowHeader } from './approval-flow-page-components/ApprovalFlowHeader';
import { ApprovalFlowInvoicesList } from './approval-flow-page-components/ApprovalFlowInvoicesList';
import { useSelectedInvoiceStateParam } from './approval-flow-page-hooks/use-selected-invoice-state-param';

const ApprovalFlowPage: React.FC = () => {
  const [invoiceFilter, setInvoiceFilter] = useState<ApprovalFlowInvoiceTypes[]>([]);
  const { data: invoiceList, isLoading: isLoadingInvoices } = useGetApprovalFlowInvoicesList(invoiceFilter);
  const [currentInvoice, setCurrentInvoice] = useState<
    ItemOf<GetApprovalFlowInvoicesListResponse> | undefined
  >(isNotEmptyArray(invoiceList) ? invoiceList[0] : undefined);
  const { isLoading: isLoadingCurrentInvoice } = useDynamicInvoice(
    currentInvoice?.invoiceId ?? '',
    'Invoice',
    { suspense: false },
  );
  const [isSidebarOpen, toggleSidebar] = useToggle(true);
  useSelectedInvoiceStateParam(currentInvoice?.invoiceId);

  useEffect(() => {
    if (isNotEmptyArray(invoiceList)) {
      const invoiceExists = invoiceList.some((invoice) => invoice.invoiceId === currentInvoice?.invoiceId);

      if (!invoiceExists) {
        setCurrentInvoice(invoiceList[0]);
      }
    }
  }, [invoiceList, currentInvoice?.invoiceId]);

  const getInvoiceHeaderText = () => {
    if (isLoadingInvoices && isNullOrUndefined(currentInvoice)) {
      return <Skeleton width={200} sx={{ fontSize: '1.5rem' }} />;
    }

    return currentInvoice ? `${currentInvoice.customerName} — ${currentInvoice.invoiceName}` : '';
  };

  const setCurrentInvoiceById = (invoiceId: string) => {
    const newInvoice = invoiceList?.find((invoice) => invoice.invoiceId === invoiceId);
    setCurrentInvoice(newInvoice);
  };

  return (
    <VyPageLayout
      path={['Invoices', 'Monthly Invoices Approval', getInvoiceHeaderText()]}
      title={
        <ApprovalFlowHeader
          isCollapseOpen={isSidebarOpen}
          handleToggle={() => toggleSidebar()}
          onInvoiceChange={setCurrentInvoice}
          invoiceData={invoiceList}
          selectedInvoiceId={currentInvoice?.invoiceId}
        >
          <ApprovalFlowInvoicesList
            invoicesData={invoiceList}
            selectedInvoiceId={currentInvoice?.invoiceId}
            onInvoiceClick={setCurrentInvoiceById}
            onFiltersChange={setInvoiceFilter}
          />
        </ApprovalFlowHeader>
      }
      sx={{ padding: '0', overflow: 'hidden' }}
    >
      <Stack
        direction="row"
        sx={{
          overflow: "hidden",
          height: "100%",
          marginRight: isSidebarOpen ? '354px' : '54px'
        }}>
        <Box
          sx={{
            width: "100%",
            position: "relative",
            overflowY: 'auto'
          }}>
          <Container maxWidth="lg" sx={{ paddingX: '24px' }}>
            {isNotNullOrUndefined(currentInvoice) && !isLoadingCurrentInvoice ? (
              <DynamicInvoiceViewPage noRedirectOnApprove id={currentInvoice.invoiceId} idType="Invoice" />
            ) : (
              <Stack spacing={2} sx={{
                paddingTop: "40px"
              }}>
                <VyLoader height="132px" />
              </Stack>
            )}
          </Container>
        </Box>
      </Stack>
    </VyPageLayout>
  );
};

const ApprovalFlowPageWithGlobalSearchParams = withGlobalSearchParams(ApprovalFlowPage);
export { ApprovalFlowPageWithGlobalSearchParams as ApprovalFlowPage };
